import { useState, Fragment } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Box, Button, IconButton, TextField, Typography, Tooltip, Divider, Input } from '@mui/material';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Utils
import messages from '../../static/messages';
import { nullClear } from '../../utils/cleaners';

// Services
import proposalsServices from '../../services/proposalsServices';

// Styles
import { button } from '../../static/theme-styles';

const ProposalNotes = ({ notesList, id, refresh }) => {
  const confirm = useConfirm();
  const userRole = JSON.parse(localStorage.getItem('user')).role;
  const [comment, setComment] = useState();
  const [editableNote, setEditableNote] = useState();

  // Action functions
  const onEditNote = (el) => setEditableNote(el);

  const onCancelEditNote = () => {
    confirm({
      title: messages.confirm_message.close_drawer,
      description: messages.confirm_message.close_drawer_description,
    })
      .then(() => setEditableNote(null))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Handler functions
  const handleChangeNote = (e) => {
    setEditableNote((prevState) => ({
      ...prevState,
      note: e.target.value,
    }));
  };

  const handleRemove = (noteId) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('Note'),
    })
      .then(() => removeNote(noteId))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Async functions
  const addNewNote = async () => {
    const notes = [
      ...nullClear(notesList),
      {
        note: comment,
      },
    ];

    try {
      const response = await proposalsServices.update(id, { notesForInstallers: notes });

      if (response.status === 200) {
        setComment(null);
        toast.success(messages.success.update.note.save);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateNote = async () => {
    const notes = [
      ...nullClear(notesList.filter((el) => el.id !== editableNote.id)),
      {
        id: editableNote.id,
        note: editableNote.note,
      },
    ];

    try {
      const response = await proposalsServices.update(id, { notesForInstallers: notes });

      if (response.status === 200) {
        setEditableNote(null);
        toast.success(messages.success.update.note.save);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const removeNote = async (noteId) => {
    const notes = [...nullClear(notesList.filter((el) => el.id !== noteId))];

    try {
      const response = await proposalsServices.update(id, { notesForInstallers: notes });

      if (response.status === 200) {
        toast.success(messages.success.update.note.remove);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>Note for Installers:</Typography>
      {notesList.map((el) => (
        <Fragment key={el.id}>
          <Box alignItems="center" sx={{ display: 'flex', mt: 1 }}>
            {editableNote?.id === el.id ? (
              <Input
                defaultValue={editableNote.note}
                name="editableNote"
                id="editableNote"
                onChange={handleChangeNote}
                autoFocus
                sx={{
                  width: 500,
                  '&::before': {
                    display: 'none',
                  },
                }}
              />
            ) : (
              <Typography variant="body1">
                {el.note}
                {` `}
                <Typography variant="caption" sx={{ fontStyle: 'italic ' }}>
                  (Created {moment.utc(el.createdAt).format('L, h:mm:ss a')})
                </Typography>
              </Typography>
            )}

            <Box sx={{ ml: 1 }}>
              {editableNote?.id === el.id ? (
                <Fragment>
                  <Tooltip title="Save">
                    <IconButton onClick={updateNote}>
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton onClick={onCancelEditNote}>
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                </Fragment>
              ) : (
                <Fragment>
                  <Tooltip title="Edit Note" disabled={userRole !== 'superadmin'}>
                    <IconButton onClick={() => onEditNote(el)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Note" disabled={userRole !== 'superadmin'}>
                    <IconButton onClick={() => handleRemove(el.id)}>
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                </Fragment>
              )}
            </Box>
          </Box>
          <Divider sx={{ mt: 1 }} />
        </Fragment>
      ))}

      <Box sx={{ mt: 3 }}>
        <TextField
          id="new-note"
          label="New Note"
          multiline
          rows={2}
          value={comment || ''}
          fullWidth
          onChange={(e) => setComment(e.target.value)}
        />

        <Button
          variant="contained"
          size="small"
          sx={{ ...button('secondary'), mt: 3 }}
          onClick={addNewNote}
          disabled={!comment}
        >
          Add Note
        </Button>
      </Box>
    </Box>
  );
};

export default ProposalNotes;
